// @bun
// src/store.ts
var MAX_ATTRIBUTE_COUNT = 128;
var MAX_ATTRIBUTE_KEY_LENGTH = 256;
var MAX_ATTRIBUTE_STRING_LENGTH = 4096;
var MAX_EVENT_COUNT = 128;
var MAX_LINK_COUNT = 128;
var MAX_NAME_LENGTH = 512;
var SECRET_ATTRIBUTE = /(^|[._-])(authorization|cookie|credential|password|secret|token|api[._-]?key)($|[._-])/i;
var validTraceId = (value) => /^[0-9a-f]{32}$/i.test(value);
var validSpanId = (value) => /^[0-9a-f]{16}$/i.test(value);
var bounded = (value, maximum) => value.length <= maximum ? value : value.slice(0, maximum);
var sanitizeUrl = (value) => {
  try {
    const url = new URL(value);
    url.hash = "";
    url.search = "";
    return url.toString();
  } catch {
    return value.split(/[?#]/u, 1)[0] ?? "";
  }
};
var scalar = (value, key, redact) => {
  if (typeof value === "boolean" || typeof value === "number")
    return Number.isFinite(value) || typeof value === "boolean" ? value : undefined;
  if (typeof value !== "string")
    return;
  const safe = /(^|[._-])url$/i.test(key) ? sanitizeUrl(value) : value;
  return bounded(redact(safe), MAX_ATTRIBUTE_STRING_LENGTH);
};
var attributesFrom = (input, redact) => {
  const entries = [];
  for (const [rawKey, rawValue] of Object.entries(input ?? {})) {
    if (entries.length >= MAX_ATTRIBUTE_COUNT)
      break;
    const key = bounded(rawKey, MAX_ATTRIBUTE_KEY_LENGTH);
    if (!key || SECRET_ATTRIBUTE.test(key))
      continue;
    if (Array.isArray(rawValue)) {
      const values = rawValue.slice(0, MAX_ATTRIBUTE_COUNT).map((value2) => scalar(value2, key, redact)).filter((value2) => value2 !== undefined);
      if (values.length > 0)
        entries.push([key, values]);
      continue;
    }
    const value = scalar(rawValue, key, redact);
    if (value !== undefined)
      entries.push([key, value]);
  }
  return Object.fromEntries(entries);
};
var unixNano = ([seconds, nanoseconds]) => (BigInt(seconds) * 1000000000n + BigInt(nanoseconds)).toString();
var durationNano = ([seconds, nanoseconds]) => (BigInt(seconds) * 1000000000n + BigInt(nanoseconds)).toString();
var traceState = (context) => {
  const encoded = context.traceState?.serialize().trim();
  return encoded ? bounded(encoded, 512) : undefined;
};
var projectStoredSpan = (span, options = {}) => {
  const context = span.spanContext();
  if (!validTraceId(context.traceId) || !validSpanId(context.spanId))
    throw new Error("Telemetry span has an invalid trace or span id");
  const parent = span.parentSpanContext;
  if (parent && !validSpanId(parent.spanId))
    throw new Error("Telemetry span has an invalid parent span id");
  const redact = options.redact ?? ((value) => value);
  const resourceAttributes = attributesFrom(span.resource.attributes, redact);
  const configuredService = options.serviceName?.trim();
  const resourceService = resourceAttributes["service.name"];
  const serviceName = bounded(configuredService || (typeof resourceService === "string" ? resourceService : "unknown-service"), MAX_NAME_LENGTH);
  const statusMessage = span.status.message ? bounded(redact(span.status.message), MAX_ATTRIBUTE_STRING_LENGTH) : undefined;
  const encodedTraceState = traceState(context);
  return {
    attributes: attributesFrom(span.attributes, redact),
    durationNano: durationNano(span.duration),
    endedAtUnixNano: unixNano(span.endTime),
    events: span.events.slice(0, MAX_EVENT_COUNT).map((event) => ({
      attributes: attributesFrom(event.attributes, redact),
      name: bounded(redact(event.name), MAX_NAME_LENGTH),
      timeUnixNano: unixNano(event.time)
    })),
    kind: span.kind,
    links: span.links.slice(0, MAX_LINK_COUNT).flatMap((link) => {
      if (!validTraceId(link.context.traceId) || !validSpanId(link.context.spanId))
        return [];
      const encodedLinkTraceState = traceState(link.context);
      return [
        {
          attributes: attributesFrom(link.attributes, redact),
          spanId: link.context.spanId.toLowerCase(),
          traceId: link.context.traceId.toLowerCase(),
          ...encodedLinkTraceState ? { traceState: encodedLinkTraceState } : {}
        }
      ];
    }),
    name: bounded(redact(span.name), MAX_NAME_LENGTH),
    ...parent ? { parentSpanId: parent.spanId.toLowerCase() } : {},
    resourceAttributes,
    scopeName: bounded(span.instrumentationScope?.name ?? "unknown-scope", MAX_NAME_LENGTH),
    ...span.instrumentationScope?.version ? {
      scopeVersion: bounded(span.instrumentationScope.version, MAX_NAME_LENGTH)
    } : {},
    serviceName,
    spanId: context.spanId.toLowerCase(),
    startedAtUnixNano: unixNano(span.startTime),
    statusCode: span.status.code,
    ...statusMessage ? { statusMessage } : {},
    traceFlags: context.traceFlags,
    traceId: context.traceId.toLowerCase(),
    ...encodedTraceState ? { traceState: encodedTraceState } : {}
  };
};
var createTraceStoreSpanExporter = (options) => {
  let closed = false;
  return {
    export: (spans, callback) => {
      if (closed) {
        callback({
          code: 1,
          error: new Error("Trace store exporter is closed")
        });
        return;
      }
      Promise.resolve().then(() => options.store.write(spans.map((span) => projectStoredSpan(span, options.projection)))).then(() => callback({ code: 0 }), (error) => {
        options.onError?.(error, spans.length);
        callback({
          code: 1,
          error: error instanceof Error ? error : new Error(String(error))
        });
      });
    },
    forceFlush: () => Promise.resolve(),
    shutdown: () => {
      closed = true;
      return Promise.resolve();
    }
  };
};
var createMemoryTraceStore = (options = {}) => {
  const maximum = Math.max(1, Math.floor(options.maxSpans ?? 1e4));
  const spans = new Map;
  const keyOf = (span) => `${span.traceId}:${span.spanId}`;
  const ordered = () => [...spans.values()].sort((left, right) => BigInt(left.startedAtUnixNano) < BigInt(right.startedAtUnixNano) ? -1 : 1);
  const store = {
    getTrace: async (traceId) => ordered().filter((span) => span.traceId === traceId.toLowerCase()),
    listTraces: async (filter = {}) => {
      const grouped = new Map;
      for (const span of ordered()) {
        if (filter.serviceName && span.serviceName !== filter.serviceName)
          continue;
        if (filter.statusCode !== undefined && span.statusCode !== filter.statusCode)
          continue;
        if (filter.sinceUnixNano && BigInt(span.startedAtUnixNano) < BigInt(filter.sinceUnixNano))
          continue;
        if (filter.untilUnixNano && BigInt(span.startedAtUnixNano) > BigInt(filter.untilUnixNano))
          continue;
        const trace = grouped.get(span.traceId) ?? [];
        trace.push(span);
        grouped.set(span.traceId, trace);
      }
      return [...grouped.entries()].map(([traceId, trace]) => {
        const startedAt = trace.reduce((value, span) => BigInt(span.startedAtUnixNano) < BigInt(value) ? span.startedAtUnixNano : value, trace[0].startedAtUnixNano);
        const endedAt = trace.reduce((value, span) => BigInt(span.endedAtUnixNano) > BigInt(value) ? span.endedAtUnixNano : value, trace[0].endedAtUnixNano);
        return {
          durationNano: (BigInt(endedAt) - BigInt(startedAt)).toString(),
          endedAtUnixNano: endedAt,
          errorSpanCount: trace.filter((span) => span.statusCode === 2).length,
          rootName: trace.find((span) => span.parentSpanId === undefined)?.name ?? trace[0].name,
          serviceNames: [...new Set(trace.map((span) => span.serviceName))],
          spanCount: trace.length,
          startedAtUnixNano: startedAt,
          traceId
        };
      }).sort((left, right) => BigInt(left.startedAtUnixNano) > BigInt(right.startedAtUnixNano) ? -1 : 1).slice(0, Math.max(1, Math.min(filter.limit ?? 100, 1000)));
    },
    prune: async (beforeUnixNano) => {
      let deleted = 0;
      for (const [key, span] of spans) {
        if (BigInt(span.startedAtUnixNano) >= BigInt(beforeUnixNano))
          continue;
        spans.delete(key);
        deleted++;
      }
      return deleted;
    },
    write: async (batch) => {
      for (const span of batch)
        spans.set(keyOf(span), span);
      while (spans.size > maximum) {
        const oldest = ordered()[0];
        if (!oldest)
          break;
        spans.delete(keyOf(oldest));
      }
    }
  };
  return store;
};

// src/index.ts
var SpanKind = {
  INTERNAL: 0,
  SERVER: 1,
  CLIENT: 2,
  PRODUCER: 3,
  CONSUMER: 4
};
var SpanStatusCode = {
  UNSET: 0,
  OK: 1,
  ERROR: 2
};
var NOOP_SPAN_CONTEXT = {
  spanId: "0000000000000000",
  traceFlags: 0,
  traceId: "00000000000000000000000000000000"
};
var noopSpan = {
  addEvent: () => noopSpan,
  end: () => {},
  isRecording: () => false,
  recordException: () => {},
  setAttribute: () => noopSpan,
  setAttributes: () => noopSpan,
  setStatus: () => noopSpan,
  spanContext: () => NOOP_SPAN_CONTEXT,
  updateName: () => noopSpan
};
var createNoopSpan = () => noopSpan;
var startActiveSpanNoop = (_name, optionsOrFn, maybeFn) => {
  const fn = typeof optionsOrFn === "function" ? optionsOrFn : maybeFn;
  return fn(noopSpan);
};
var noopTracer = {
  startActiveSpan: startActiveSpanNoop,
  startSpan: () => noopSpan
};
var createNoopTracer = () => noopTracer;
var createNoopTracerProvider = () => ({
  getTracer: () => noopTracer
});
var tracerOrNoop = (provider, name, version) => provider !== undefined ? provider.getTracer(name, version) : noopTracer;
var ABS_ATTRS = {
  tenant: "abs.tenant",
  shardId: "abs.shard.id",
  engineId: "abs.engine.id",
  collection: "abs.collection",
  mutation: "abs.mutation",
  mutationAttempt: "abs.mutation.attempt",
  subscriptionId: "abs.subscription.id",
  batchSize: "abs.batch.size",
  clusterMessageOrigin: "abs.cluster.origin",
  jobId: "abs.job.id",
  jobKind: "abs.job.kind",
  jobAttempt: "abs.job.attempt",
  jobMaxAttempts: "abs.job.max_attempts",
  workerId: "abs.worker.id",
  runtimeKey: "abs.runtime.key",
  runtimePid: "abs.runtime.pid",
  runtimePort: "abs.runtime.port",
  runtimeExitReason: "abs.runtime.exit_reason",
  runtimeReadinessMs: "abs.runtime.readiness_ms",
  routeShard: "abs.route.shard",
  routeDecision: "abs.route.decision",
  secretName: "abs.secret.name",
  secretFingerprint: "abs.secret.fingerprint",
  auditKind: "abs.audit.kind",
  handoffCorrelationId: "abs.handoff.correlation_id",
  handoffOperation: "abs.handoff.operation",
  handoffOutcome: "abs.handoff.outcome",
  handoffService: "abs.handoff.service",
  handoffSource: "abs.handoff.source",
  handoffAttempt: "abs.handoff.attempt",
  handoffContradiction: "abs.handoff.contradiction",
  agentRunId: "abs.agent.run.id",
  agentParentRunId: "abs.agent.run.parent_id",
  agentStatus: "abs.agent.run.status",
  agentDescriptorId: "abs.agent.descriptor.id",
  agentDescriptorVersion: "abs.agent.descriptor.version",
  agentDescriptorDigest: "abs.agent.descriptor.digest",
  agentActorTenantId: "abs.agent.actor.tenant_id",
  agentActorUserId: "abs.agent.actor.user_id",
  agentActorAgentId: "abs.agent.actor.agent_id",
  agentDelegationId: "abs.agent.delegation.id",
  agentStepId: "abs.agent.step.id",
  agentStepSequence: "abs.agent.step.sequence",
  agentStepKind: "abs.agent.step.kind",
  agentEffectName: "abs.agent.effect.name",
  agentWorkerId: "abs.agent.worker.id",
  agentBudgetActions: "abs.agent.budget.actions",
  agentBudgetCostMicros: "abs.agent.budget.cost_micros",
  agentBudgetInputTokens: "abs.agent.budget.input_tokens",
  agentBudgetOutputTokens: "abs.agent.budget.output_tokens",
  agentBudgetSpendMinor: "abs.agent.budget.spend_minor",
  agentBudgetWallTimeMs: "abs.agent.budget.wall_time_ms"
};
var handoffSpanAttributes = (evidence) => ({
  [ABS_ATTRS.handoffCorrelationId]: evidence.correlationId,
  [ABS_ATTRS.handoffOperation]: evidence.operation,
  [ABS_ATTRS.handoffOutcome]: evidence.outcome,
  [ABS_ATTRS.handoffService]: evidence.service,
  [ABS_ATTRS.handoffSource]: evidence.source,
  ...evidence.attempt === undefined ? {} : { [ABS_ATTRS.handoffAttempt]: evidence.attempt }
});
var handoffSummarySpanAttributes = (summary) => ({
  [ABS_ATTRS.handoffContradiction]: summary.contradiction,
  [ABS_ATTRS.handoffCorrelationId]: summary.correlationId,
  [ABS_ATTRS.handoffOperation]: summary.operation,
  [ABS_ATTRS.handoffOutcome]: summary.status,
  [ABS_ATTRS.handoffService]: summary.service
});
var withSpan = async (tracer, name, options, fn) => tracer.startActiveSpan(name, options, async (span) => {
  try {
    const result = await fn(span);
    span.setStatus({ code: SpanStatusCode.OK });
    return result;
  } catch (error) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error)
    });
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
});
var withSpanSync = (tracer, name, options, fn) => tracer.startActiveSpan(name, options, (span) => {
  try {
    const result = fn(span);
    span.setStatus({ code: SpanStatusCode.OK });
    return result;
  } catch (error) {
    span.setStatus({
      code: SpanStatusCode.ERROR,
      message: error instanceof Error ? error.message : String(error)
    });
    span.recordException(error);
    throw error;
  } finally {
    span.end();
  }
});
var readActiveTraceId = async () => {
  try {
    const specifier = ["@opentelemetry", "api"].join("/");
    const otel = await import(specifier).catch(() => null);
    if (otel === null)
      return;
    const span = otel.trace.getActiveSpan?.();
    if (!span)
      return;
    const ctx = span.spanContext?.();
    return ctx?.traceId;
  } catch {
    return;
  }
};
export {
  withSpanSync,
  withSpan,
  tracerOrNoop,
  readActiveTraceId,
  projectStoredSpan,
  handoffSummarySpanAttributes,
  handoffSpanAttributes,
  createTraceStoreSpanExporter,
  createNoopTracerProvider,
  createNoopTracer,
  createNoopSpan,
  createMemoryTraceStore,
  SpanStatusCode,
  SpanKind,
  ABS_ATTRS
};

//# debugId=E188540533BF789B64756E2164756E21
//# sourceMappingURL=index.js.map
